import ReactGA from 'react-ga4';

let gaMeasurementId = null;
let pendingUserId = null;

const applyGAUserId = () => {
  if (!gaMeasurementId) return;

  const params = { user_id: pendingUserId };

  // Prefer react-ga4's gtag wrapper when available.
  try {
    if (typeof ReactGA.gtag === 'function') {
      ReactGA.gtag('config', gaMeasurementId, params);
      return;
    }
  } catch (_) {
    // Fall back to window.gtag below.
  }

  // Fallback: call the global gtag directly.
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('config', gaMeasurementId, params);
    }
  } catch (_) {
    // non-blocking
  }
};

// Initialize Google Analytics
export const initGA = () => {
  gaMeasurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-PLACEHOLDER123';
  
  ReactGA.initialize(gaMeasurementId, {
    gtagOptions: {
      debug_mode: process.env.NODE_ENV !== 'production'
    }
  });
  
  console.log('Google Analytics initialized with ID:', gaMeasurementId);

  // Apply any pending user_id set before GA initialized.
  applyGAUserId();
};

// Set/clear GA4 user_id for cross-session/device stitching.
// IMPORTANT: Never pass PII (email/username). Use your internal non-PII user id.
export const setGAUserId = (userId) => {
  pendingUserId = userId || null;
  applyGAUserId();
};

export const clearGAUserId = () => {
  pendingUserId = null;
  applyGAUserId();
};

// Custom analytics hook
export const useAnalytics = () => {
  const trackPageView = (page) => {
    ReactGA.send({
      hitType: 'pageview',
      page: page
    });
  };

  // Custom event: authentication started (e.g., requested magic link / code)
  // NOTE: We intentionally do NOT use GA4's built-in `sign_up` for this,
  // because `sign_up` should represent a completed registration.
  const trackAuthStart = (method = 'magic_link', eventParams = {}) => {
    ReactGA.event('auth_start', {
      method,
      ...eventParams
    });
    console.log('Tracked auth start:', method, eventParams);
  };

  const trackLogin = (method = 'magic_link') => {
    ReactGA.event('login', {
      method: method,
      engagement_time: Date.now()
    });
    console.log('Tracked login event:', method);
  };

  const trackSignUp = (method = 'magic_link') => {
    ReactGA.event('sign_up', {
      method: method
    });
    console.log('Tracked sign up event:', method);
  };

  const trackReviewCreation = (sandwichId, restaurantId, rating) => {
    ReactGA.event('review_created', {
      sandwich_id: sandwichId,
      restaurant_id: restaurantId,
      rating: rating,
      content_type: 'review'
    });
    console.log('Tracked review creation:', { sandwichId, restaurantId, rating });
  };

  const trackSandwichUpload = (sandwichId, restaurantId, hasPhoto) => {
    ReactGA.event('sandwich_upload', {
      sandwich_id: sandwichId,
      restaurant_id: restaurantId,
      has_photo: hasPhoto,
      content_type: 'sandwich'
    });
    console.log('Tracked sandwich upload:', { sandwichId, restaurantId, hasPhoto });
  };

  const trackLike = (reviewId, isLiked) => {
    ReactGA.event(isLiked ? 'like' : 'unlike', {
      content_type: 'review',
      content_id: reviewId
    });
    console.log('Tracked like event:', { reviewId, isLiked });
  };

  const trackComment = (reviewId) => {
    ReactGA.event('comment', {
      content_type: 'review',
      content_id: reviewId
    });
    console.log('Tracked comment event:', reviewId);
  };

  const trackCommentDeleted = (reviewId, commentId) => {
    ReactGA.event('comment_deleted', {
      content_type: 'review',
      content_id: reviewId,
      comment_id: commentId
    });
    console.log('Tracked comment deleted event:', { reviewId, commentId });
  };

  const trackSearch = (query, type, resultsCount) => {
    ReactGA.event('search', {
      search_term: query,
      content_type: type,
      results_count: resultsCount
    });
    console.log('Tracked search event:', { query, type, resultsCount });
  };

  const trackRestaurantView = (restaurantId, restaurantName) => {
    ReactGA.event('view_item', {
      item_category: 'restaurant',
      item_id: restaurantId,
      item_name: restaurantName
    });
    console.log('Tracked restaurant view:', { restaurantId, restaurantName });
  };

  const trackSandwichView = (sandwichId, sandwichName, restaurantName) => {
    ReactGA.event('view_item', {
      item_category: 'sandwich',
      item_id: sandwichId,
      item_name: sandwichName,
      restaurant_name: restaurantName
    });
    console.log('Tracked sandwich view:', { sandwichId, sandwichName, restaurantName });
  };

  const trackFollow = (targetType, targetId, isFollowing) => {
    ReactGA.event(isFollowing ? 'follow' : 'unfollow', {
      target_type: targetType, // 'user' or 'restaurant'
      target_id: targetId
    });
    console.log('Tracked follow event:', { targetType, targetId, isFollowing });
  };

  const trackShare = (contentType, contentId, shareMethod) => {
    ReactGA.event('share', {
      content_type: contentType,
      content_id: contentId,
      method: shareMethod
    });
    console.log('Tracked share event:', { contentType, contentId, shareMethod });
  };

  const trackEvent = (eventName, eventParams = {}) => {
    ReactGA.event(eventName, eventParams);
    console.log('Tracked event:', eventName, eventParams);
  };

  return {
    trackPageView,
    trackAuthStart,
    trackLogin,
    trackSignUp,
    trackReviewCreation,
    trackSandwichUpload,
    trackLike,
    trackComment,
    trackCommentDeleted,
    trackSearch,
    trackRestaurantView,
    trackSandwichView,
    trackFollow,
    trackShare,
    trackEvent
  };
};
